import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, RefreshCw, AlertTriangle, ArrowUpDown, ChevronUp, ChevronDown, Download, Eye } from 'lucide-react';
import { InvoicePdfButton } from '../components/InvoicePdfButton';
import {
  Bar, BarChart, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, LabelList,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { DashboardLayout } from '../components/DashboardLayout';
import { reportsAPI, brosAPI, invoicesAPI } from '../lib/api';
import { usePortfolioPrefsStore } from '../store';
import { formatCurrency, cn, deriveInvoiceStatus } from '../lib/utils';
import { formatCompact, currencySymbol } from '../lib/chartFormatters';
import {
  pdfCurrencyFormatter, drawBrandedHeader, drawBrandedFooter,
  TABLE_DEFAULTS, addFormattedSheet, EXCEL_CURRENCY_FORMAT,
} from '../lib/exportUtils';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errors';
import { convertAmount } from '../lib/currencyConvert';
import { CurrencyToggle } from '../components/CurrencyToggle';
import { ReconciliationDrilldown } from '../components/ReconciliationDrilldown';
import { BillingDrilldownModal } from '../components/BillingDrilldownModal';
import {
  monthKeyToLabel,
  generateMonthAxis,
  ForecastBarShape,
  ForecastReferenceLine,
  ForecastCaveat,
  buildAxisData,
} from '../lib/forecastChartUtils';
import { BROSOverviewChart } from '../components/bros/BROSOverviewChart';
import { ARAgingAnalysis } from '../components/bros/ARAgingAnalysis';
import { InvoiceReconciliationCard } from '../components/bros/InvoiceReconciliationCard';
import { invoicePaidAmount } from '../lib/invoicePaid';
import {
  getOutstandingInvoices, exportClientStatementPDF, exportClientStatementExcel,
} from '../lib/clientStatementExport';

// Ex-VAT invoice value and paid amount (paid net of VAT pro rata to the paid
// share), the basis of the BR OS dashboard Cashflow tab.
function exVatInvoiceAmounts(inv) {
  const gross = Number(inv.total_amount) || 0;
  const net = inv.subtotal != null && inv.subtotal !== '' ? (Number(inv.subtotal) || 0) : gross;
  const share = gross ? net / gross : 1;
  return { net, paid: invoicePaidAmount(inv) * share };
}

// ── Constants ──────────────────────────────────────────────────────────

const PERIODS = [
  { value: 'ytd', label: 'YTD' },
  { value: 'last_12m', label: 'Last 12M' },
  { value: 'all', label: 'All Time' },
];

const TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'reconciliation', label: 'Bookings vs Invoices' },
  { value: 'payments', label: 'Invoices vs Payments' },
];

const STATUS_CONFIG = {
  paid: {
    label: 'Paid',
    badge: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 border-0',
    heading: 'text-green-700 dark:text-green-400',
  },
  unpaid: {
    label: 'Unpaid',
    badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 border-0',
    heading: 'text-amber-700 dark:text-amber-400',
  },
  overdue: {
    label: 'Overdue',
    badge: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 border-0',
    heading: 'text-red-700 dark:text-red-400',
  },
  draft: {
    label: 'Draft',
    badge: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400 border-0',
    heading: 'text-gray-700 dark:text-gray-400',
  },
  cancelled: {
    label: 'Cancelled',
    badge: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-0',
    heading: 'text-red-600 dark:text-red-400',
  },
  void: {
    label: 'Void',
    badge: 'bg-gray-100 text-gray-600 dark:bg-gray-900/30 dark:text-gray-400 border-0',
    heading: 'text-gray-600 dark:text-gray-400',
  },
};

const STATUS_ORDER = ['overdue', 'unpaid', 'paid', 'draft', 'cancelled', 'void'];

// ── Helpers ───────────────────────────────────────────────────────────

function monthKeyToLongLabel(mk) {
  if (!mk || mk.length < 7) return mk;
  const [year, month] = mk.split('-');
  const date = new Date(Number(year), Number(month) - 1, 1);
  return date.toLocaleString('default', { month: 'long', year: 'numeric' });
}

// ── Shared pill controls ───────────────────────────────────────────────

function PeriodPills(props) {
  const { period, onPeriodChange } = props;
  return (
    <div className="flex gap-0.5 bg-muted rounded-md p-0.5">
      {PERIODS.map((p) => (
        <button
          key={p.value}
          onClick={() => onPeriodChange(p.value)}
          className={cn(
            'px-3 py-1.5 text-sm font-medium rounded transition-all',
            period === p.value
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

// ── Tooltips ────────────────────────────────────────────────────────────

function BillingTooltip(props) {
  const { active, payload, label, currency } = props;
  if (!active || !payload?.length) return null;
  const bookingsVal = payload.find((p) => p.dataKey === 'Bookings')?.value;
  const pipelineVal = payload.find((p) => p.dataKey === 'Pipeline')?.value;
  const invoicedVal = payload.find((p) => p.dataKey === 'Invoiced')?.value;
  const totalBookings = (bookingsVal ?? 0) + (pipelineVal ?? 0);
  const delta = totalBookings - (invoicedVal ?? 0);
  const rollup = payload[0]?.payload?._rollup;
  return (
    <div className="rounded-lg border bg-card p-3 shadow-md text-sm">
      <p className="font-medium mb-1">{label}</p>
      {bookingsVal != null && (
        <p style={{ color: '#3B82F6' }}>
          {pipelineVal ? 'Confirmed forecast' : 'Bookings (by creation)'}: {formatCurrency(bookingsVal, currency)}
        </p>
      )}
      {payload[0]?.payload?.Cancellation ? (
        <p className="text-muted-foreground text-xs">
          incl. cancellation fees: {formatCurrency(payload[0].payload.Cancellation, currency)}
        </p>
      ) : null}
      {pipelineVal != null && pipelineVal > 0 && (
        <p style={{ color: '#93C5FD' }}>Pipeline (open enquiries): {formatCurrency(pipelineVal, currency)}</p>
      )}
      {pipelineVal != null && pipelineVal > 0 && (
        <p className="text-muted-foreground text-xs">Total: {formatCurrency(totalBookings, currency)}</p>
      )}
      {invoicedVal != null && (
        <p style={{ color: '#F59E0B' }}>Invoiced (by issue date): {formatCurrency(invoicedVal, currency)}</p>
      )}
      {rollup && rollup.invoiced_against_bookings != null && (
        <p className="text-green-600 dark:text-green-400">
          Invoiced against these bookings: {formatCurrency(rollup.invoiced_against_bookings, currency)}
          {rollup.coverage_pct != null ? ` (${Math.round(rollup.coverage_pct)}%)` : ''}
        </p>
      )}
      <p className="text-muted-foreground mt-1 border-t pt-1">
        Issue-date delta: {formatCurrency(delta, currency)}
        {delta > 0 ? ' (sold > billed)' : delta < 0 ? ' (catch-up billing)' : ''}
      </p>
    </div>
  );
}

function CashflowTooltip(props) {
  const { active, payload, label, currency } = props;
  if (!active || !payload?.length) return null;
  const invoicedVal = payload.find((p) => p.dataKey === 'Invoiced')?.value;
  const paidVal = payload.find((p) => p.dataKey === 'Paid')?.value;
  return (
    <div className="rounded-lg border bg-card p-3 shadow-md text-sm">
      <p className="font-medium mb-1">{label}</p>
      {invoicedVal != null && (
        <p style={{ color: '#F59E0B' }}>Invoiced: {formatCurrency(invoicedVal, currency)}</p>
      )}
      {paidVal != null && (
        <p style={{ color: '#10B981' }}>Paid: {formatCurrency(paidVal, currency)}</p>
      )}
      {invoicedVal != null && paidVal != null && invoicedVal > 0 && (
        <p className="text-muted-foreground mt-1 border-t pt-1 text-xs">
          Collection rate: {Math.round((paidVal / invoicedVal) * 100)}%
        </p>
      )}
    </div>
  );
}

// ── Sortable header ─────────────────────────────────────────────────────

function SortableHeader(props) {
  const { label, field, align, sortKey, sortAsc, onSort } = props;
  return (
    <TableHead
      className="cursor-pointer select-none"
      onClick={() => onSort(field)}
    >
      <div className={cn('flex items-center gap-1', align === 'right' && 'justify-end')}>
        {label}
        {sortKey === field ? (
          sortAsc
            ? <ChevronUp className="h-3 w-3 text-muted-foreground" />
            : <ChevronDown className="h-3 w-3 text-muted-foreground" />
        ) : (
          <ArrowUpDown className="h-3 w-3 text-muted-foreground" />
        )}
      </div>
    </TableHead>
  );
}

// ── Build BVS chart data (shared between Overview and Reconciliation tabs) ──

function buildBvsChartData(bvsData, period) {
  if (!bvsData) return [];
  const byMonth = new Map();
  const axis = generateMonthAxis(period);
  if (axis) {
    for (const mk of axis) {
      byMonth.set(mk, {
        month: monthKeyToLabel(mk),
        monthKey: mk,
        Bookings: 0,
        Pipeline: 0,
        Invoiced: 0,
        _isForecast: false,
      });
    }
  }
  const actualsSet = new Set(bvsData.actuals_months || []);
  const now = new Date();
  const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  for (const mk of (bvsData.months || [])) {
    const existing = byMonth.get(mk);
    const entry = existing || {
      month: monthKeyToLabel(mk),
      monthKey: mk,
      Bookings: 0,
      Pipeline: 0,
      Invoiced: 0,
      _isForecast: false,
    };
    entry.Bookings = bvsData.bookings?.[mk] ?? 0;
    // Cancellation fees are inside Bookings (Fan, 2026-10-24); kept apart
    // only so the tooltip can say how much.
    entry.Cancellation = bvsData.cancellations?.[mk] ?? 0;
    entry.Invoiced = bvsData.invoiced?.[mk] ?? 0;
    byMonth.set(mk, entry);
  }
  for (const mk of (bvsData.forecast_months || [])) {
    if (mk <= currentMonthKey) continue;
    const fcVal = bvsData.bookings_forecast?.[mk] ?? 0;
    const existing = byMonth.get(mk);
    if (existing) {
      existing.Bookings = (existing.Bookings ?? 0) + fcVal;
      if (!actualsSet.has(mk)) existing._isForecast = true;
    } else {
      byMonth.set(mk, {
        month: monthKeyToLabel(mk),
        monthKey: mk,
        Bookings: fcVal,
        Pipeline: 0,
        Invoiced: 0,
        _isForecast: true,
      });
    }
  }
  const pipelineForecast = bvsData.pipeline_forecast || {};
  for (const mk of Object.keys(pipelineForecast)) {
    if (mk <= currentMonthKey) continue;
    const pVal = pipelineForecast[mk] ?? 0;
    const existing = byMonth.get(mk);
    if (existing) {
      existing.Pipeline = pVal;
      if (!actualsSet.has(mk)) existing._isForecast = true;
    } else {
      byMonth.set(mk, {
        month: monthKeyToLabel(mk),
        monthKey: mk,
        Bookings: 0,
        Pipeline: pVal,
        Invoiced: 0,
        _isForecast: true,
      });
    }
  }
  return Array.from(byMonth.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, v]) => v);
}

// ── Export functions (mirror PP scope pattern) ──────────────────────

async function exportRevenueExcel({ activeTab, dc, bvsChartData, cfChartData, invoices, cxInv }) {
  const XLSX = await import('xlsx');
  const wb = XLSX.utils.book_new();
  const round2 = (v) => v != null ? Math.round(v * 100) / 100 : '';
  const curSuffix = dc === 'EUR' ? '-eur' : '';
  const CUR = EXCEL_CURRENCY_FORMAT;

  if (activeTab === 'overview') {
    const dataRows = (bvsChartData || []).filter(d => !d._isForecast);
    const bvsAoa = [['Month', 'Bookings', 'Invoiced', 'Delta']];
    let totB = 0, totI = 0;
    for (const d of dataRows) {
      const b = round2(d.Bookings); const inv = round2(d.Invoiced);
      totB += b || 0; totI += inv || 0;
      bvsAoa.push([d.month, b, inv, round2((d.Bookings ?? 0) - (d.Invoiced ?? 0))]);
    }
    bvsAoa.push(['Total', round2(totB), round2(totI), round2(totB - totI)]);
    addFormattedSheet(wb, XLSX, 'Bookings vs Invoiced', bvsAoa, { formats: { 1: CUR, 2: CUR, 3: CUR } });

    const cfAoa = [['Month', 'Invoiced', 'Paid']];
    for (const d of (cfChartData || [])) cfAoa.push([d.month, round2(d.Invoiced), round2(d.Paid)]);
    if (cfAoa.length > 1) addFormattedSheet(wb, XLSX, 'Invoiced vs Paid', cfAoa, { formats: { 1: CUR, 2: CUR } });
  } else if (activeTab === 'bookings-invoices') {
    const dataRows = (bvsChartData || []).filter(d => !d._isForecast);
    const aoa = [['Month', 'Bookings', 'Invoiced', 'Delta']];
    let totB = 0, totI = 0;
    for (const d of dataRows) {
      const b = round2(d.Bookings); const inv = round2(d.Invoiced);
      totB += b || 0; totI += inv || 0;
      aoa.push([d.month, b, inv, round2((d.Bookings ?? 0) - (d.Invoiced ?? 0))]);
    }
    aoa.push(['Total', round2(totB), round2(totI), round2(totB - totI)]);
    addFormattedSheet(wb, XLSX, 'Reconciliation', aoa, { formats: { 1: CUR, 2: CUR, 3: CUR } });
  } else {
    const aoa = [['Company', 'Invoice', 'Issue Date', 'Due Date', 'Amount', 'Paid', 'Outstanding', 'Status', 'Currency']];
    for (const inv of (invoices || [])) {
      const amt = round2(cxInv ? cxInv(inv.total_amount, inv.currency) : inv.total_amount);
      const paid = round2(cxInv ? cxInv(invoicePaidAmount(inv), inv.currency) : invoicePaidAmount(inv));
      aoa.push([
        inv.company || '', inv.bros_id || '', inv.issue_date || '', inv.due_date || '',
        amt, paid, round2((amt || 0) - (paid || 0)),
        deriveInvoiceStatus(inv), dc,
      ]);
    }
    if (aoa.length > 1) addFormattedSheet(wb, XLSX, 'Client Invoices', aoa, { formats: { 4: CUR, 5: CUR, 6: CUR } });
  }

  if (wb.SheetNames.length === 0) {
    addFormattedSheet(wb, XLSX, 'Empty', [['Note'], ['No data to export']]);
  }

  const fileName = `bros-revenue-${activeTab}${curSuffix}.xlsx`;
  XLSX.writeFile(wb, fileName);
}

async function exportRevenuePDF({ activeTab, dc, bvsChartData, cfChartData, invoices, cxInv }) {
  const { default: jsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const fmtV = pdfCurrencyFormatter(dc);
  const curSuffix = dc === 'EUR' ? '-eur' : '';
  const tabLabel = activeTab === 'overview' ? 'Overview' : activeTab === 'bookings-invoices' ? 'Bookings vs Invoices' : 'Invoices vs Payments';

  let y = drawBrandedHeader(doc, {
    title: 'Bonjour Residences',
    scopeLine: `Revenue: ${tabLabel} (${dc})`,
  });

  if (activeTab === 'overview' || activeTab === 'bookings-invoices') {
    const dataRows = (bvsChartData || []).filter(d => !d._isForecast);
    const rows = dataRows.map(d => [
      d.month,
      fmtV(d.Bookings),
      fmtV(d.Invoiced),
      fmtV((d.Bookings ?? 0) - (d.Invoiced ?? 0)),
    ]);
    if (rows.length) {
      const totBookings = dataRows.reduce((s, d) => s + (d.Bookings ?? 0), 0);
      const totInvoiced = dataRows.reduce((s, d) => s + (d.Invoiced ?? 0), 0);
      rows.push([
        { content: 'Total', styles: { fontStyle: 'bold' } },
        { content: fmtV(totBookings), styles: { fontStyle: 'bold' } },
        { content: fmtV(totInvoiced), styles: { fontStyle: 'bold' } },
        { content: fmtV(totBookings - totInvoiced), styles: { fontStyle: 'bold' } },
      ]);
      autoTable(doc, {
        ...TABLE_DEFAULTS,
        startY: y,
        head: [['Month', 'Bookings', 'Invoiced', 'Delta']],
        body: rows,
      });
      y = doc.lastAutoTable.finalY + 8;
    }
    if (activeTab === 'overview' && cfChartData?.length) {
      const cfRows = cfChartData.map(d => [d.month, fmtV(d.Invoiced), fmtV(d.Paid)]);
      autoTable(doc, {
        ...TABLE_DEFAULTS,
        startY: y,
        head: [['Month', 'Invoiced', 'Paid']],
        body: cfRows,
      });
    }
  } else {
    const rows = (invoices || []).map(inv => [
      inv.company || '',
      inv.bros_id || '',
      inv.issue_date || '',
      inv.due_date || '',
      fmtV(cxInv ? cxInv(inv.total_amount, inv.currency) : inv.total_amount),
      deriveInvoiceStatus(inv),
    ]);
    if (rows.length) {
      autoTable(doc, {
        ...TABLE_DEFAULTS,
        startY: y,
        head: [['Company', 'Invoice', 'Issue Date', 'Due Date', 'Amount', 'Status']],
        body: rows,
      });
    }
  }

  drawBrandedFooter(doc);

  const fileName = `bros-revenue-${activeTab}${curSuffix}.pdf`;
  doc.save(fileName);
}

// ── Overview Tab ────────────────────────────────────────────────────────

function OverviewTab(props) {
  const {
    period, dc,
    bvsData, bvsLoading, reconData, reconLoading,
    clientInvoices, clientInvoicesLoading, cxInv,
    onBvsBarClick,
  } = props;

  const bvsChartData = useMemo(
    () => buildBvsChartData(bvsData, period),
    [bvsData, period]
  );

  const bvsHasForecast = useMemo(
    () => bvsChartData.some((d) => d._isForecast),
    [bvsChartData]
  );

  const bvsHasPipeline = useMemo(
    () => bvsChartData.some((d) => d.Pipeline > 0),
    [bvsChartData]
  );

  const bvsChartDataWithCoverage = useMemo(() => {
    if (!reconData?.rollups) return bvsChartData;
    return bvsChartData.map((d) => {
      const rollup = reconData.rollups[d.monthKey];
      if (!rollup) return d;
      return { ...d, coverage_pct: rollup.coverage_pct, _rollup: rollup };
    });
  }, [bvsChartData, reconData]);

  // Cashflow chart: every invoice's total_amount in Invoiced, amount_paid
  // in Paid, both bucketed by due date.  A month with invoices due but
  // unpaid shows Paid falling short of Invoiced (the overdue signal).
  const cfChartData = useMemo(() => {
    const monthMap = {};
    for (const inv of (clientInvoices || [])) {
      const bucketDate = inv.due_date || inv.issue_date;
      if (!bucketDate) continue;
      const mk = bucketDate.slice(0, 7);
      if (!monthMap[mk]) monthMap[mk] = { Invoiced: 0, Paid: 0 };
      // Ex VAT, paid net of VAT pro rata: same basis and figures as the
      // dashboard Cashflow tab and the P&L (2026-10-25: same basis everywhere).
      const { net, paid } = exVatInvoiceAmounts(inv);
      monthMap[mk].Invoiced += cxInv(net, inv.currency);
      monthMap[mk].Paid += cxInv(paid, inv.currency);
    }
    return buildAxisData(period, monthMap, ['Invoiced', 'Paid']);
  }, [clientInvoices, cxInv, period]);

  const bvsIsLoading = bvsLoading || reconLoading;

  // Totals for Bookings vs Invoiced footer
  const bvsTotals = useMemo(() => {
    let totalBookings = 0;
    let totalInvoiced = 0;
    for (const d of bvsChartDataWithCoverage) {
      if (!d._isForecast) {
        totalBookings += d.Bookings ?? 0;
        totalInvoiced += d.Invoiced ?? 0;
      }
    }
    return { bookings: totalBookings, invoiced: totalInvoiced, diff: totalBookings - totalInvoiced };
  }, [bvsChartDataWithCoverage]);

  // Totals for Invoiced vs Paid footer
  const cfTotals = useMemo(() => {
    let totalInvoiced = 0;
    let totalPaid = 0;
    for (const d of cfChartData) {
      totalInvoiced += d.Invoiced ?? 0;
      totalPaid += d.Paid ?? 0;
    }
    return { invoiced: totalInvoiced, paid: totalPaid, outstanding: totalInvoiced - totalPaid };
  }, [cfChartData]);

  // BVS series config for BROSOverviewChart
  const bvsSeries = useMemo(() => {
    const result = [
      {
        dataKey: 'Bookings',
        name: 'Bookings',
        color: '#3B82F6',
        stackId: 'bookings',
        labelContent: ({ x, y, width, value, index }) => {
          const entry = bvsChartDataWithCoverage[index];
          if (entry?.Pipeline > 0) return null;
          if (!value || value < 100) return null;
          // Coverage % is on the reconciliation engine's own base (table
          // below), not this bar's, so it is no longer printed on the bar.
          const covPct = null;
          return (
            <g>
              <text x={x + width / 2} y={y - 6} textAnchor="middle" fontSize={10} fill="#3B82F6">
                {formatCompact(value, dc)}
              </text>
              {covPct != null && (
                <text
                  x={x + width / 2}
                  y={y - 18}
                  textAnchor="middle"
                  fontSize={9}
                  fill={covPct >= 95 ? '#16a34a' : covPct >= 70 ? '#d97706' : '#dc2626'}
                  fontWeight="600"
                >
                  {Math.round(covPct)}%
                </text>
              )}
            </g>
          );
        },
      },
    ];
    if (bvsHasPipeline) {
      result.push({
        dataKey: 'Pipeline',
        name: 'Pipeline (open enquiries)',
        color: '#93C5FD',
        stackId: 'bookings',
        fillOpacity: 0.5,
        stroke: '#93C5FD',
        strokeDasharray: '4 2',
        strokeWidth: 1,
        useShape: false,
        labelContent: ({ x, y, width, value, index }) => {
          if (!value || value <= 0) return null;
          const entry = bvsChartDataWithCoverage[index];
          const total = (entry?.Bookings ?? 0) + value;
          if (!total || total < 100) return null;
          return (
            <text x={x + width / 2} y={y - 6} textAnchor="middle" fontSize={10} fill="#3B82F6">
              {formatCompact(total, dc)}
            </text>
          );
        },
      });
    }
    result.push({ dataKey: 'Invoiced', name: 'Invoiced', color: '#F59E0B' });
    return result;
  }, [bvsChartDataWithCoverage, bvsHasPipeline, dc]);

  return (
    <div className="grid grid-cols-1 gap-6">
      {/* Chart 1: Bookings vs Invoices (via BROSOverviewChart) */}
      <BROSOverviewChart
        data={bvsChartDataWithCoverage}
        series={bvsSeries}
        title="Bookings vs Invoices"
        caption="Bookings by closing date, the Sales tab's basis without the rolling projection, incl. cancellation fees (ex VAT). Invoiced = client invoices by issue date, ex VAT."
        subtitle={
          bvsHasPipeline ? (
            <p className="text-xs text-muted-foreground mt-0.5">
              Pipeline = open enquiries at full value (not probability-weighted).
              {bvsData?.pipeline_excluded_no_check_in?.count > 0 && (
                <> {bvsData.pipeline_excluded_no_check_in.count} enquiry(ies) excluded (no check-in date).</>
              )}
            </p>
          ) : null
        }
        dc={dc}
        loading={bvsIsLoading && !bvsData}
        emptyMessage="No bookings vs invoices data for this period."
        tooltipContent={<BillingTooltip currency={dc} />}
        footerLabel="YTD Bookings vs Invoiced"
        footerItems={[
          { label: 'Bookings', value: bvsTotals.bookings, color: '#3B82F6' },
          { label: 'Invoiced', value: bvsTotals.invoiced, color: '#F59E0B' },
          { label: 'Difference', value: bvsTotals.diff, color: '#000000',
            className: bvsTotals.diff < 0 ? 'text-red-600 dark:text-red-400' : bvsTotals.diff > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-foreground' },
        ]}
        hasForecast={bvsHasForecast}
        onBarClick={(dataKey, barData) => onBvsBarClick(dataKey)(barData)}
        growthDataKey="Bookings"
      />

      {/* Chart 2: Invoices vs Client Payments (via BROSOverviewChart) */}
      <BROSOverviewChart
        data={cfChartData}
        series={[
          { dataKey: 'Invoiced', name: 'Invoiced', color: '#F59E0B' },
          { dataKey: 'Paid', name: 'Paid', color: '#10B981' },
        ]}
        title="Invoices vs Client Payments"
        caption="Ex VAT, same figures as the dashboard Cashflow tab. Invoiced by due date. Paid (net of VAT) attributed to the invoice's due-date bucket (not the payment date). A shortfall in Paid signals missing or overdue payment."
        dc={dc}
        loading={clientInvoicesLoading && clientInvoices.length === 0}
        emptyMessage="No invoicing and payments data for this period."
        tooltipContent={<CashflowTooltip currency={dc} />}
        footerLabel="YTD Invoiced vs Paid"
        footerItems={[
          { label: 'Invoiced', value: cfTotals.invoiced, color: '#F59E0B' },
          { label: 'Paid', value: cfTotals.paid, color: '#10B981' },
          { label: 'Outstanding', value: cfTotals.outstanding, color: '#000000',
            className: cfTotals.outstanding > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-foreground' },
        ]}
        growthDataKey="Invoiced"
      />

      {/* Chart 3: AR Aging Analysis */}
      <ARAgingAnalysis
        invoices={clientInvoices}
        cxInv={cxInv}
        dc={dc}
        loading={clientInvoicesLoading}
      />

      {/* Diagnostics: due-date vs issue-date reconciliation for current month */}
      <InvoiceReconciliationCard
        invoices={clientInvoices}
        cxInv={cxInv}
        dc={dc}
        targetMonth={new Date().toISOString().slice(0, 7)}
      />
    </div>
  );
}

// ── Reconciliation Tab (PP Bookings-tab layout) ─────────────────────

function ReconciliationTab(props) {
  const {
    period, dc,
    bvsData, bvsLoading, reconData, reconLoading,
  } = props;

  const [expandedMonths, setExpandedMonths] = useState(new Set());

  const bvsChartData = useMemo(
    () => buildBvsChartData(bvsData, period),
    [bvsData, period]
  );

  // Build monthly rows with reconciliation data merged
  const monthlyRows = useMemo(() => {
    return bvsChartData
      .filter((d) => !d._isForecast)
      .map((d) => {
        const rollup = reconData?.rollups?.[d.monthKey] ?? null;
        return {
          monthKey: d.monthKey,
          month: d.month,
          bookings: d.Bookings ?? 0,
          invoiced: d.Invoiced ?? 0,
          delta: (d.Bookings ?? 0) - (d.Invoiced ?? 0),
          coverage_pct: rollup?.coverage_pct ?? null,
          booking_count: rollup?.booking_count ?? null,
          unexplained_delta: rollup?.unexplained_delta ?? null,
          invoiced_against_bookings: rollup?.invoiced_against_bookings ?? null,
          status_counts: rollup?.status_counts ?? null,
          explained_delta: rollup?.explained_delta ?? null,
          rollup,
        };
      });
  }, [bvsChartData, reconData]);

  // Totals across non-forecast months
  const totals = useMemo(() => {
    let totalBookings = 0;
    let totalInvoiced = 0;
    let monthCount = 0;
    for (const r of monthlyRows) {
      totalBookings += r.bookings;
      totalInvoiced += r.invoiced;
      if (r.bookings > 0 || r.invoiced > 0) monthCount++;
    }
    return {
      bookings: totalBookings,
      invoiced: totalInvoiced,
      delta: totalBookings - totalInvoiced,
      monthCount,
    };
  }, [monthlyRows]);

  const isLoading = bvsLoading || reconLoading;

  const toggleMonth = useCallback((mk) => {
    setExpandedMonths((prev) => {
      const next = new Set(prev);
      next.has(mk) ? next.delete(mk) : next.add(mk);
      return next;
    });
  }, []);

  if (isLoading && !bvsData) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="reconciliation-tab">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Total Bookings</p>
            <p className="text-xl font-bold font-mono mt-1">{formatCurrency(totals.bookings, dc)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Total Invoiced</p>
            <p className="text-xl font-bold font-mono mt-1">{formatCurrency(totals.invoiced, dc)}</p>
            <p className="text-xs text-muted-foreground mt-0.5">excl. VAT</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Difference</p>
            <p className={cn(
              'text-xl font-bold font-mono mt-1',
              totals.delta < 0 ? 'text-red-600 dark:text-red-400' : totals.delta > 0 ? 'text-amber-600 dark:text-amber-400' : ''
            )}>
              {formatCurrency(totals.delta, dc)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Months with Activity</p>
            <p className="text-xl font-bold font-mono mt-1">{totals.monthCount}</p>
          </CardContent>
        </Card>
      </div>

      {/* Monthly expandable table (PP Bookings-tab structure) */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Month</TableHead>
                  <TableHead className="text-right">Bookings</TableHead>
                  <TableHead className="text-right">Invoiced</TableHead>
                  <TableHead className="text-right">Delta</TableHead>
                  <TableHead className="text-right">Coverage</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {monthlyRows.map((r) => {
                  const hasData = r.bookings > 0 || r.invoiced > 0;
                  const isExpanded = expandedMonths.has(r.monthKey);
                  const covColor = r.coverage_pct == null ? '' :
                    r.coverage_pct >= 95 ? 'text-green-600 dark:text-green-400' :
                    r.coverage_pct >= 70 ? 'text-amber-600 dark:text-amber-400' :
                    'text-red-600 dark:text-red-400';

                  return (
                    <React.Fragment key={r.monthKey}>
                      <TableRow
                        className={cn(hasData && 'cursor-pointer hover:bg-muted/50')}
                        onClick={hasData ? () => toggleMonth(r.monthKey) : undefined}
                      >
                        <TableCell className="w-8 px-2">
                          {hasData && (isExpanded
                            ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
                            : <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          )}
                        </TableCell>
                        <TableCell className="font-medium">
                          {r.month}
                          {r.booking_count != null && (
                            <span className="ml-2 text-xs text-muted-foreground">
                              {r.booking_count} booking{r.booking_count !== 1 ? 's' : ''}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {hasData ? formatCurrency(r.bookings, dc) : '\u2014'}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {hasData ? formatCurrency(r.invoiced, dc) : '\u2014'}
                        </TableCell>
                        <TableCell className={cn(
                          'text-right font-mono text-sm',
                          r.delta < 0 ? 'text-red-600 dark:text-red-400' : r.delta > 0 ? 'text-amber-600 dark:text-amber-400' : ''
                        )}>
                          {hasData ? formatCurrency(r.delta, dc) : '\u2014'}
                        </TableCell>
                        <TableCell className={cn('text-right font-mono text-sm font-semibold', covColor)}>
                          {r.coverage_pct != null ? `${Math.round(r.coverage_pct)}%` : '\u2014'}
                        </TableCell>
                      </TableRow>

                      {/* Expanded detail */}
                      {isExpanded && r.rollup && (
                        <TableRow className="bg-muted/20">
                          <TableCell></TableCell>
                          <TableCell colSpan={5} className="py-3">
                            <div className="space-y-3">
                              {/* Status counts */}
                              {r.rollup.status_counts && (
                                <div className="flex flex-wrap gap-2">
                                  {Object.entries(r.rollup.status_counts).map(([status, count]) => (
                                    <span
                                      key={status}
                                      className="text-xs px-2 py-0.5 rounded-full border"
                                    >
                                      {status.replace(/_/g, ' ')}: {count}
                                    </span>
                                  ))}
                                </div>
                              )}
                              {/* Explained delta */}
                              {r.rollup.explained_delta && Object.keys(r.rollup.explained_delta).length > 0 && (
                                <div className="text-xs text-muted-foreground">
                                  <span className="font-medium">Explained delta: </span>
                                  {Object.entries(r.rollup.explained_delta).map(([reason, val]) => (
                                    <span key={reason} className="mr-3">
                                      {reason.replace(/_/g, ' ')}: {formatCurrency(val, dc)}
                                    </span>
                                  ))}
                                </div>
                              )}
                              {/* Per-booking drilldown */}
                              <ReconciliationDrilldown
                                expanded={true}
                                onClose={() => toggleMonth(r.monthKey)}
                                month={r.monthKey}
                                dc={dc}
                                rollup={r.rollup}
                              />
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}

                {/* Total row */}
                <TableRow className="border-t-2 font-semibold bg-muted/30">
                  <TableCell></TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell className="text-right font-mono">{formatCurrency(totals.bookings, dc)}</TableCell>
                  <TableCell className="text-right font-mono">{formatCurrency(totals.invoiced, dc)}</TableCell>
                  <TableCell className={cn(
                    'text-right font-mono',
                    totals.delta < 0 ? 'text-red-600 dark:text-red-400' : totals.delta > 0 ? 'text-amber-600 dark:text-amber-400' : ''
                  )}>
                    {formatCurrency(totals.delta, dc)}
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Payments Tab ────────────────────────────────────────────────────────

function PaymentsTab(props) {
  const { dc, fxRates, invoicesRef, exportScopeRef, clientInvoices, clientInvoicesLoading, cxInv: parentCxInv, onRefresh } = props;
  // Use parent-provided invoices if available, otherwise fall back to internal fetch
  const [localInvoices, setLocalInvoices] = useState([]);
  const [localLoading, setLocalLoading] = useState(!clientInvoices);
  const invoices = clientInvoices || localInvoices;
  const loading = clientInvoices ? clientInvoicesLoading : localLoading;
  const [sortKey, setSortKey] = useState('due_date');
  const [sortAsc, setSortAsc] = useState(true);
  const [viewMode, setViewMode] = useState('by-client');
  const [expandedMonths, setExpandedMonths] = useState(new Set());
  const [expandedClients, setExpandedClients] = useState(new Set());
  const [pdfMatchMap, setPdfMatchMap] = useState({});

  // Use parent cxInv if provided, otherwise create local one
  const localCxInv = useCallback((amount, invCurrency) => {
    if (amount == null) return 0;
    return convertAmount(amount, invCurrency || 'GBP', dc, fxRates) || 0;
  }, [dc, fxRates]);
  const cxInv = parentCxInv || localCxInv;

  // Sync invoices to the ref for export scoping
  useEffect(() => {
    if (invoicesRef) invoicesRef.current = invoices;
  }, [invoices, invoicesRef]);

  // Pre-populate PDF renderability via BR OS (the authority).
  // Replaces the db.invoices-based pdf-match-batch which missed ~200
  // invoices that exist in BR OS but are not in the FA's synced copy.
  useEffect(() => {
    const brosIds = invoices
      .filter((inv) => inv.bros_id)
      .map((inv) => inv.bros_id);
    if (brosIds.length === 0) {
      setPdfMatchMap({});
      return;
    }
    invoicesAPI.pdfRenderableBatch(brosIds).then((res) => {
      setPdfMatchMap(res.data?.matches || {});
    }).catch(() => {
      // Non-critical; buttons fall back to optimistic
    });
  }, [invoices]);

  const byStatus = useMemo(() => {
    const groups = {};
    for (const inv of invoices) {
      const s = deriveInvoiceStatus(inv);
      if (!groups[s]) groups[s] = [];
      groups[s].push(inv);
    }
    return groups;
  }, [invoices]);

  const kpis = useMemo(() => {
    const statusKpis = STATUS_ORDER
      .filter((s) => byStatus[s] && byStatus[s].length > 0)
      .map((s) => ({
        status: s,
        count: byStatus[s].length,
        total: byStatus[s].reduce((sum, inv) => sum + cxInv(inv.total_amount, inv.currency), 0),
      }));
    return statusKpis;
  }, [byStatus, cxInv]);

  // Stat card totals.  Outstanding comes from the canonical per-invoice
  // outstanding_amount supplied by the backend (services.receivables_dedup),
  // the SAME predicate the A&L balance sheet uses: null for paid/void/draft,
  // else total_amount - amount_paid floored at zero.  This surface must not
  // re-derive an outstanding predicate inline.
  const cardTotals = useMemo(() => {
    let totalIssued = 0;
    let totalOutstanding = 0;
    let overdueAmount = 0;
    let overdueCount = 0;
    for (const inv of invoices) {
      totalIssued += cxInv(inv.total_amount, inv.currency);
      const outstanding = inv.outstanding_amount != null
        ? cxInv(inv.outstanding_amount, inv.currency)
        : 0;
      totalOutstanding += outstanding;
      if (deriveInvoiceStatus(inv) === 'overdue') {
        overdueAmount += outstanding;
        overdueCount++;
      }
    }
    const overduePct = totalOutstanding > 0 ? (overdueAmount / totalOutstanding) * 100 : null;
    return { totalIssued, totalOutstanding, overdueAmount, overdueCount, overduePct };
  }, [invoices, cxInv]);

  // Group by client, overdue clients first, with per-client aggregates
  const clientGroups = useMemo(() => {
    const map = new Map();
    for (const inv of invoices) {
      const client = inv.company || '(unknown)';
      if (!map.has(client)) {
        map.set(client, { client, invoices: [], hasOverdue: false, invoiced: 0, paid: 0, outstanding: 0, overdueCount: 0, overdueAmount: 0 });
      }
      const group = map.get(client);
      group.invoices.push(inv);
      const invAmount = cxInv(inv.total_amount, inv.currency);
      const paidAmount = cxInv(invoicePaidAmount(inv), inv.currency);
      // Outstanding uses the canonical per-invoice field (excludes void/paid),
      // so the per-client rows reconcile with the corrected card total.
      const outstanding = inv.outstanding_amount != null
        ? cxInv(inv.outstanding_amount, inv.currency)
        : 0;
      group.invoiced += invAmount;
      group.paid += paidAmount;
      group.outstanding += outstanding;
      if (deriveInvoiceStatus(inv) === 'overdue') {
        group.hasOverdue = true;
        group.overdueCount++;
        group.overdueAmount += outstanding;
      }
    }
    const groups = Array.from(map.values());
    // Sort: overdue clients first, then alphabetical
    groups.sort((a, b) => {
      if (a.hasOverdue && !b.hasOverdue) return -1;
      if (!a.hasOverdue && b.hasOverdue) return 1;
      return a.client.localeCompare(b.client);
    });
    // Within each client, sort: overdue first, then by due_date
    for (const g of groups) {
      g.invoices.sort((a, b) => {
        const aOverdue = deriveInvoiceStatus(a) === 'overdue' ? 0 : 1;
        const bOverdue = deriveInvoiceStatus(b) === 'overdue' ? 0 : 1;
        if (aOverdue !== bOverdue) return aOverdue - bOverdue;
        return (a.due_date || '').localeCompare(b.due_date || '');
      });
    }
    return groups;
  }, [invoices, cxInv]);

  // Monthly groups for By Month view
  const monthlyGroups = useMemo(() => {
    const byMonth = new Map();
    for (const inv of invoices) {
      const bucketDate = inv.due_date || inv.issue_date;
      if (!bucketDate) continue;
      const mk = bucketDate.slice(0, 7);
      if (!byMonth.has(mk)) {
        byMonth.set(mk, { monthKey: mk, invoices: [], invoiced: 0, paid: 0, overdueCount: 0, overdueAmount: 0 });
      }
      const group = byMonth.get(mk);
      group.invoices.push(inv);
      const amt = cxInv(inv.total_amount, inv.currency);
      const paid = cxInv(invoicePaidAmount(inv), inv.currency);
      group.invoiced += amt;
      group.paid += paid;
      if (deriveInvoiceStatus(inv) === 'overdue') {
        group.overdueCount++;
        group.overdueAmount += amt - paid;
      }
    }
    return Array.from(byMonth.values()).sort((a, b) => b.monthKey.localeCompare(a.monthKey));
  }, [invoices, cxInv]);

  const toggleMonth = useCallback((mk) => {
    setExpandedMonths((prev) => {
      const next = new Set(prev);
      next.has(mk) ? next.delete(mk) : next.add(mk);
      return next;
    });
  }, []);

  const toggleClient = useCallback((clientName) => {
    setExpandedClients((prev) => {
      const next = new Set(prev);
      next.has(clientName) ? next.delete(clientName) : next.add(clientName);
      return next;
    });
  }, []);

  // Keep export scope ref in sync for scoped exports
  useEffect(() => {
    if (exportScopeRef) {
      exportScopeRef.current = { viewMode, expandedClients, clientGroups };
    }
  }, [exportScopeRef, viewMode, expandedClients, clientGroups]);

  const handleSort = useCallback((key) => {
    setSortKey((prev) => {
      if (prev === key) {
        setSortAsc((a) => !a);
        return key;
      }
      setSortAsc(true);
      return key;
    });
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const sharedSortProps = { sortKey, sortAsc, onSort: handleSort };

  return (
    <div className="space-y-6" data-testid="payments-tab">
      {/* KPI stat cards (currency-aware via shared toggle) */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4" data-testid="payments-kpi-row">
        {/* Total Issued: amount headline, count subline */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <Badge className="text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 border-0">Total Issued</Badge>
            </div>
            <div className="text-xl font-bold font-mono mt-2" data-testid="total-issued-amount">
              {formatCurrency(cardTotals.totalIssued, dc)}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">{invoices.length} invoice{invoices.length !== 1 ? 's' : ''} · incl. VAT</p>
          </CardContent>
        </Card>
        {/* Total Outstanding */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <Badge className="text-xs bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 border-0">Total Outstanding</Badge>
            </div>
            <div className={cn('text-xl font-bold font-mono mt-2', cardTotals.totalOutstanding > 0 ? 'text-amber-700 dark:text-amber-400' : '')}>
              {formatCurrency(cardTotals.totalOutstanding, dc)}
            </div>
          </CardContent>
        </Card>
        {kpis.map((k) => (
          <Card key={k.status}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Badge className={cn('text-xs', (STATUS_CONFIG[k.status] || {}).badge)}>{(STATUS_CONFIG[k.status] || {}).label || k.status}</Badge>
                <span className="text-xs text-muted-foreground">{k.count} invoice{k.count !== 1 ? 's' : ''}</span>
              </div>
              <div className={cn('text-xl font-bold font-mono mt-2', (STATUS_CONFIG[k.status] || {}).heading)}>
                {formatCurrency(k.total, dc)}
              </div>
              {k.status === 'overdue' && cardTotals.overduePct != null && (
                <p className="text-xs text-muted-foreground mt-0.5" data-testid="overdue-pct-of-outstanding">
                  {Math.round(cardTotals.overduePct)}% of outstanding
                </p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Client invoices table with By Client / By Month toggle */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Client Invoices</CardTitle>
            <div className="flex items-center gap-2">
              <div className="flex gap-0.5 bg-muted rounded-md p-0.5" data-testid="payments-view-toggle">
                {[{ value: 'by-client', label: 'By Client' }, { value: 'by-month', label: 'By Month' }].map((v) => (
                  <button
                    key={v.value}
                    onClick={() => setViewMode(v.value)}
                    className={cn(
                      'px-2.5 py-1 text-xs font-medium rounded transition-all',
                      viewMode === v.value
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    )}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading}>
                <RefreshCw className={cn('h-4 w-4 mr-2', loading && 'animate-spin')} />
                Refresh
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            {invoices.length} invoice{invoices.length !== 1 ? 's' : ''} across {clientGroups.length} client{clientGroups.length !== 1 ? 's' : ''}
            {dc !== 'GBP' && ' (amounts converted to ' + dc + ')'}
          </p>
          {viewMode === 'by-month' && (
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Grouped by due date (issue date when due date is unavailable). Paid from amount_paid.
            </p>
          )}
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            {viewMode === 'by-client' && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead className="text-right">Invoiced</TableHead>
                  <TableHead className="text-right">Paid</TableHead>
                  <TableHead className="text-right">Outstanding</TableHead>
                  <TableHead className="text-right">Overdue</TableHead>
                  <TableHead className="text-right">Overdue Amount</TableHead>
                  <TableHead className="text-center">Export</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clientGroups.map((group) => {
                  const isExpanded = expandedClients.has(group.client);
                  const hasOutstanding = getOutstandingInvoices(group.invoices).length > 0;
                  return (
                  <React.Fragment key={group.client}>
                    {/* Client aggregate row (expandable) */}
                    <TableRow
                      className={cn(
                        'cursor-pointer hover:bg-muted/50',
                        group.hasOverdue && 'border-l-2 border-l-red-500'
                      )}
                      onClick={() => toggleClient(group.client)}
                      data-testid={`client-row-${group.client}`}
                    >
                      <TableCell className="w-8 px-2">
                        {isExpanded
                          ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
                          : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                      </TableCell>
                      <TableCell className="font-medium text-sm">
                        {group.client}
                        <span className="ml-2 text-xs text-muted-foreground">
                          {group.invoices.length} invoice{group.invoices.length !== 1 ? 's' : ''}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {formatCurrency(group.invoiced, dc)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm text-green-600 dark:text-green-400">
                        {formatCurrency(group.paid, dc)}
                      </TableCell>
                      <TableCell className={cn(
                        'text-right font-mono text-sm',
                        group.outstanding > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-foreground'
                      )}>
                        {formatCurrency(group.outstanding, dc)}
                      </TableCell>
                      <TableCell className="text-right text-sm">
                        {group.overdueCount > 0 ? (
                          <Badge className="text-[10px] px-1.5 py-0 bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 border-0">
                            {group.overdueCount}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">{'\u2014'}</span>
                        )}
                      </TableCell>
                      <TableCell className={cn(
                        'text-right font-mono text-sm',
                        group.overdueAmount > 0 ? 'text-red-600 dark:text-red-400' : 'text-foreground'
                      )}>
                        {group.overdueAmount > 0 ? formatCurrency(group.overdueAmount, dc) : '\u2014'}
                      </TableCell>
                      <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                        {hasOutstanding && (
                          <div className="flex items-center justify-center gap-1">
                            <button
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded border border-input bg-background hover:bg-accent hover:text-accent-foreground transition-colors"
                              title={`Export PDF statement for ${group.client}`}
                              data-testid={`client-export-pdf-${group.client}`}
                              onClick={() => exportClientStatementPDF({ client: group.client, invoices: group.invoices })}
                            >
                              <Download className="h-3 w-3" />
                              PDF
                            </button>
                            <button
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded border border-input bg-background hover:bg-accent hover:text-accent-foreground transition-colors"
                              title={`Export Excel statement for ${group.client}`}
                              data-testid={`client-export-excel-${group.client}`}
                              onClick={() => exportClientStatementExcel({ client: group.client, invoices: group.invoices })}
                            >
                              <Download className="h-3 w-3" />
                              Excel
                            </button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                    {/* Expanded: invoice detail rows */}
                    {isExpanded && group.invoices.map((inv, i) => {
                      const cfg = STATUS_CONFIG[deriveInvoiceStatus(inv)] || {};
                      return (
                        <TableRow key={inv.bros_id || `${group.client}-${i}`} className="bg-muted/20 text-xs">
                          <TableCell></TableCell>
                          <TableCell className="py-1.5">
                            <span className="font-mono text-muted-foreground">{inv.bros_id || '-'}</span>
                            <span className="ml-2 text-muted-foreground">{inv.issue_date || ''}</span>
                            <span className="ml-2 text-muted-foreground">{inv.due_date ? `due ${inv.due_date}` : ''}</span>
                            <Badge className={cn('ml-2 text-[10px] px-1.5 py-0', cfg.badge || '')}>
                              {cfg.label || deriveInvoiceStatus(inv)}
                            </Badge>
                            <InvoicePdfButton invoice={inv} pdfMatchInfo={pdfMatchMap[inv.bros_id]} variant="icon" className="ml-2" />
                          </TableCell>
                          <TableCell className="text-right font-mono py-1.5">
                            {formatCurrency(cxInv(inv.total_amount, inv.currency), dc)}
                          </TableCell>
                          <TableCell className="text-right font-mono py-1.5 text-green-600 dark:text-green-400">
                            {formatCurrency(cxInv(invoicePaidAmount(inv), inv.currency), dc)}
                          </TableCell>
                          <TableCell className="text-right font-mono py-1.5 text-amber-600 dark:text-amber-400">
                            {formatCurrency(cxInv(inv.total_amount, inv.currency) - cxInv(invoicePaidAmount(inv), inv.currency), dc)}
                          </TableCell>
                          <TableCell></TableCell>
                          <TableCell></TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      );
                    })}
                  </React.Fragment>
                  );
                })}
                {clientGroups.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No client invoices found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            )}
            {viewMode === 'by-month' && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Month</TableHead>
                  <TableHead className="text-right">Invoices</TableHead>
                  <TableHead className="text-right">Invoiced</TableHead>
                  <TableHead className="text-right">Paid</TableHead>
                  <TableHead className="text-right">Outstanding</TableHead>
                  <TableHead className="text-right">Overdue</TableHead>
                  <TableHead className="text-right">Overdue Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {monthlyGroups.map((mg) => {
                  const isExpanded = expandedMonths.has(mg.monthKey);
                  const outstanding = mg.invoiced - mg.paid;
                  return (
                    <React.Fragment key={mg.monthKey}>
                      <TableRow
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => toggleMonth(mg.monthKey)}
                      >
                        <TableCell className="w-8 px-2">
                          {isExpanded
                            ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
                            : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                        </TableCell>
                        <TableCell className="font-medium text-sm">
                          {monthKeyToLongLabel(mg.monthKey)}
                        </TableCell>
                        <TableCell className="text-right text-sm text-muted-foreground">
                          {mg.invoices.length}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {formatCurrency(mg.invoiced, dc)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm text-green-600 dark:text-green-400">
                          {formatCurrency(mg.paid, dc)}
                        </TableCell>
                        <TableCell className={cn(
                          'text-right font-mono text-sm',
                          outstanding > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-foreground'
                        )}>
                          {formatCurrency(outstanding, dc)}
                        </TableCell>
                        <TableCell className="text-right text-sm">
                          {mg.overdueCount > 0 ? (
                            <Badge className="text-[10px] px-1.5 py-0 bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 border-0">
                              {mg.overdueCount}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">{'\u2014'}</span>
                          )}
                        </TableCell>
                        <TableCell className={cn(
                          'text-right font-mono text-sm',
                          mg.overdueAmount > 0 ? 'text-red-600 dark:text-red-400' : 'text-foreground'
                        )}>
                          {mg.overdueAmount > 0 ? formatCurrency(mg.overdueAmount, dc) : '\u2014'}
                        </TableCell>
                      </TableRow>
                      {isExpanded && mg.invoices.map((inv, i) => {
                        const cfg = STATUS_CONFIG[deriveInvoiceStatus(inv)] || {};
                        return (
                          <TableRow key={inv.bros_id || `${mg.monthKey}-${i}`} className="bg-muted/20">
                            <TableCell></TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              {inv.company || '(unknown)'} {inv.bros_id ? `(${inv.bros_id})` : ''}
                              <InvoicePdfButton invoice={inv} pdfMatchInfo={pdfMatchMap[inv.bros_id]} variant="icon" className="ml-2" />
                            </TableCell>
                            <TableCell></TableCell>
                            <TableCell className="text-right font-mono text-xs">
                              {formatCurrency(cxInv(inv.total_amount, inv.currency), dc)}
                            </TableCell>
                            <TableCell className="text-right font-mono text-xs text-green-600 dark:text-green-400">
                              {formatCurrency(cxInv(invoicePaidAmount(inv), inv.currency), dc)}
                            </TableCell>
                            <TableCell className="text-right font-mono text-xs text-amber-600 dark:text-amber-400">
                              {formatCurrency(cxInv(inv.total_amount, inv.currency) - cxInv(invoicePaidAmount(inv), inv.currency), dc)}
                            </TableCell>
                            <TableCell>
                              <Badge className={cn('text-[10px] px-1.5 py-0', cfg.badge || '')}>
                                {cfg.label || deriveInvoiceStatus(inv)}
                              </Badge>
                            </TableCell>
                            <TableCell></TableCell>
                          </TableRow>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
                {monthlyGroups.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No client invoices found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────

export function BROSRevenuePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'overview';

  const [period, setPeriod] = useState('ytd');
  const { displayCurrency } = usePortfolioPrefsStore();
  const dc = displayCurrency;

  // FX rate for client-side currency conversion (cashflow chart)
  const [fxRates, setFxRates] = useState(null);

  const gbpToEur = useMemo(() => {
    const eurGbp = fxRates?.EUR_GBP;
    return eurGbp && eurGbp > 0 ? 1 / eurGbp : null;
  }, [fxRates]);

  const cx = useCallback(
    (v) => (dc === 'EUR' && gbpToEur) ? v * gbpToEur : v,
    [dc, gbpToEur]
  );

  const [bvsData, setBvsData] = useState(null);
  const [bvsLoading, setBvsLoading] = useState(false);
  const [reconData, setReconData] = useState(null);
  const [reconLoading, setReconLoading] = useState(false);
  // Client invoices (shared between Overview and Payments tabs)
  const [clientInvoices, setClientInvoices] = useState([]);
  const [clientInvoicesLoading, setClientInvoicesLoading] = useState(false);

  // Drill state for overview tab bar clicks
  const [overviewDrillMonth, setOverviewDrillMonth] = useState(null);
  const [overviewDrillSeries, setOverviewDrillSeries] = useState(null);
  const [overviewDrillBarValue, setOverviewDrillBarValue] = useState(null);

  const fetchBvs = useCallback(async (p, currency) => {
    setBvsLoading(true);
    try {
      const res = await reportsAPI.brosBillingVsSales(p, (currency || 'GBP').toLowerCase());
      setBvsData(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBvsLoading(false);
    }
  }, []);

  const fetchRecon = useCallback(async (p, currency) => {
    setReconLoading(true);
    try {
      const res = await reportsAPI.brosBookingInvoiceRecon(p, (currency || 'GBP').toLowerCase());
      setReconData(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setReconLoading(false);
    }
  }, []);

  const fetchClientInvoices = useCallback(async () => {
    setClientInvoicesLoading(true);
    try {
      const res = await brosAPI.getInvoices({ type: 'client', limit: 5000 });
      setClientInvoices(res.data.invoices || []);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setClientInvoicesLoading(false);
    }
  }, []);

  const fetchFxRates = useCallback(async (p) => {
    try {
      const res = await reportsAPI.brosDashboard(p);
      setFxRates(res.data?.fx_rates ?? null);
    } catch (_) {
      // FX rates are best-effort; cashflow chart falls back to GBP
    }
  }, []);

  useEffect(() => {
    fetchBvs(period, dc);
    fetchRecon(period, dc);
    fetchClientInvoices();
    fetchFxRates(period);
  }, [period, dc, fetchBvs, fetchRecon, fetchClientInvoices, fetchFxRates]);

  const handleRefresh = useCallback(() => {
    fetchBvs(period, dc);
    fetchRecon(period, dc);
    fetchClientInvoices();
    fetchFxRates(period);
  }, [period, dc, fetchBvs, fetchRecon, fetchClientInvoices, fetchFxRates]);

  const handleTabChange = useCallback((tab) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    });
  }, [setSearchParams]);

  const handleOverviewBvsBarClick = useCallback((seriesName) => (barData) => {
    if (!barData?.monthKey) return;
    setOverviewDrillMonth(barData.monthKey);
    setOverviewDrillSeries(seriesName);
    setOverviewDrillBarValue(barData[seriesName] ?? null);
  }, []);

  const isLoading = bvsLoading || reconLoading || clientInvoicesLoading;

  // Per-invoice currency conversion (shared across tabs and exports).
  // Pass full fxRates map so DKK, USD, etc. convert correctly (not just EUR/GBP).
  const cxInv = useCallback((amount, invCurrency) => {
    if (amount == null) return 0;
    return convertAmount(amount, invCurrency || 'GBP', dc, fxRates) || 0;
  }, [dc, fxRates]);

  // Export state
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [isExportingExcel, setIsExportingExcel] = useState(false);
  // Payments tab invoice ref (for exports)
  const paymentsInvoicesRef = React.useRef([]);
  const paymentsExportScopeRef = React.useRef({ viewMode: 'by-client', expandedClients: new Set(), clientGroups: [] });

  // BVS chart data (shared between overview and reconciliation exports)
  const bvsChartData = useMemo(
    () => buildBvsChartData(bvsData, period),
    [bvsData, period]
  );

  // CF chart data for export (built from invoices, bucketed by due date)
  const cfChartDataForExport = useMemo(() => {
    if (!clientInvoices.length) return [];
    const monthMap = {};
    for (const inv of clientInvoices) {
      const bucketDate = inv.due_date || inv.issue_date;
      if (!bucketDate) continue;
      const mk = bucketDate.slice(0, 7);
      if (!monthMap[mk]) monthMap[mk] = { Invoiced: 0, Paid: 0 };
      // Ex VAT, paid net of VAT pro rata: same basis and figures as the
      // dashboard Cashflow tab and the P&L (2026-10-25: same basis everywhere).
      const { net, paid } = exVatInvoiceAmounts(inv);
      monthMap[mk].Invoiced += cxInv(net, inv.currency);
      monthMap[mk].Paid += cxInv(paid, inv.currency);
    }
    return buildAxisData(period, monthMap, ['Invoiced', 'Paid']);
  }, [clientInvoices, cxInv, period]);

  // Resolve export invoices: scoped to expanded client in by-client view, full set otherwise
  // Scoped export: in by-client view with exactly one client expanded,
  // export only that client's invoices. Otherwise fall back to full set.
  const getScopedExportInvoices = useCallback(() => {
    const all = paymentsInvoicesRef.current;
    const scope = paymentsExportScopeRef.current;
    if (scope.viewMode === 'by-client' && scope.expandedClients.size === 1) {
      const clientName = Array.from(scope.expandedClients)[0];
      const group = scope.clientGroups.find((g) => g.client === clientName);
      if (group) return group.invoices;
    }
    return all;
  }, []);

  const handleExportExcel = useCallback(async () => {
    setIsExportingExcel(true);
    try {
      const tabMap = { overview: 'overview', reconciliation: 'bookings-invoices', payments: 'payments' };
      await exportRevenueExcel({
        activeTab: tabMap[activeTab] || activeTab,
        dc,
        bvsChartData,
        cfChartData: cfChartDataForExport,
        invoices: getScopedExportInvoices(),
        cxInv,
      });
      toast.success('Excel exported');
    } catch (err) {
      console.error('Excel export failed:', err);
      toast.error(getErrorMessage(err));
    } finally {
      setIsExportingExcel(false);
    }
  }, [activeTab, dc, bvsChartData, cfChartDataForExport, cxInv, getScopedExportInvoices]);

  const handleExportPDF = useCallback(async () => {
    setIsExportingPDF(true);
    try {
      const tabMap = { overview: 'overview', reconciliation: 'bookings-invoices', payments: 'payments' };
      await exportRevenuePDF({
        activeTab: tabMap[activeTab] || activeTab,
        dc,
        bvsChartData,
        cfChartData: cfChartDataForExport,
        invoices: getScopedExportInvoices(),
        cxInv,
      });
      toast.success('PDF exported');
    } catch (err) {
      console.error('PDF export failed:', err);
      toast.error(getErrorMessage(err));
    } finally {
      setIsExportingPDF(false);
    }
  }, [activeTab, dc, bvsChartData, cfChartDataForExport, cxInv, getScopedExportInvoices]);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Revenue</h1>
            <p className="text-muted-foreground mt-1">
              BR OS revenue tracking: bookings, invoicing, and client payments {'\u00B7'} {dc}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <PeriodPills period={period} onPeriodChange={setPeriod} />
            <CurrencyToggle />
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportPDF}
              disabled={isExportingPDF}
              data-testid="export-pdf-btn"
            >
              {isExportingPDF
                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                : <Download className="h-4 w-4 mr-2" />}
              Export PDF
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportExcel}
              disabled={isExportingExcel}
              data-testid="export-excel-btn"
            >
              {isExportingExcel
                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                : <Download className="h-4 w-4 mr-2" />}
              Export Excel
            </Button>
            <Button variant="outline" size="icon" onClick={handleRefresh} disabled={isLoading}>
              <RefreshCw className={cn('h-4 w-4', isLoading && 'animate-spin')} />
            </Button>
          </div>
        </div>

        {/* Tab bar (underline style) */}
        <div className="border-b">
          <nav className="flex gap-0" aria-label="Revenue tabs">
            {TABS.map((tab) => (
              <button
                key={tab.value}
                onClick={() => handleTabChange(tab.value)}
                className={cn(
                  'px-4 py-2.5 text-sm font-medium border-b-2 transition-colors',
                  activeTab === tab.value
                    ? 'border-foreground text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                )}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab content */}
        {activeTab === 'overview' && (
          <>
            <OverviewTab
              period={period}
              dc={dc}
              bvsData={bvsData}
              bvsLoading={bvsLoading}
              reconData={reconData}
              reconLoading={reconLoading}
              clientInvoices={clientInvoices}
              clientInvoicesLoading={clientInvoicesLoading}
              cxInv={cxInv}
              onBvsBarClick={handleOverviewBvsBarClick}
            />
            {/* Overview drill-down modal for invoiced/pipeline bar clicks */}
            <BillingDrilldownModal
              open={!!overviewDrillMonth && overviewDrillSeries !== 'Bookings'}
              onClose={() => {
                setOverviewDrillMonth(null);
                setOverviewDrillSeries(null);
                setOverviewDrillBarValue(null);
              }}
              month={overviewDrillMonth}
              series={
                overviewDrillSeries === 'Pipeline' ? 'pipeline' : 'invoiced'
              }
              barValue={overviewDrillBarValue}
              dc={dc}
            />
          </>
        )}

        {activeTab === 'reconciliation' && (
          <ReconciliationTab
            period={period}
            dc={dc}
            bvsData={bvsData}
            bvsLoading={bvsLoading}
            reconData={reconData}
            reconLoading={reconLoading}
          />
        )}

        {activeTab === 'payments' && (
          <PaymentsTab dc={dc} fxRates={fxRates} invoicesRef={paymentsInvoicesRef} exportScopeRef={paymentsExportScopeRef} clientInvoices={clientInvoices} clientInvoicesLoading={clientInvoicesLoading} cxInv={cxInv} onRefresh={handleRefresh} />
        )}
      </div>
    </DashboardLayout>
  );
}
